/**
 * Authorization Compiler
 *
 * Compiles authorization rules from DomainDefinition resources and authz config.
 * Produces CompiledAuthzRule records for runtime enforcement.
 */

import type {
  AuthzConfig,
  ResourceDefinition,
} from "../schema/domain-definition";
import type { CompiledAuthzRule, AuthzResult, RouteContext } from "../runtime/domain-runtime";

/**
 * Compile authorization rules from resource definitions.
 *
 * Each resource's inline `authz` config is compiled into a
 * CompiledAuthzRule that can be checked at request time.
 */
export function compileAuthzRules(
  resources: Record<string, ResourceDefinition>,
  _authz: AuthzConfig
): Record<string, CompiledAuthzRule> {
  const rules: Record<string, CompiledAuthzRule> = {};

  for (const [key, resource] of Object.entries(resources)) {
    const { authz } = resource;
    rules[key] = {
      resourceKey: key,
      visibility: authz.visibility,
      requiredScopes: authz.scopes ?? [],
      requiredRoles: authz.roles ?? [],
      ownerAttribute: authz.ownerAttribute,
      ownerManagedAccess: authz.ownerManagedAccess ?? false,
    };
  }

  return rules;
}

/**
 * Check authorization for a resource access against compiled rules.
 *
 * Decision logic by visibility:
 * - public: always allowed
 * - private: requires matching role
 * - consent: requires UMA consent flow
 * - user-owned: requires owner match or UMA consent
 */
export function checkAuthz(
  rule: CompiledAuthzRule,
  context: RouteContext
): AuthzResult {
  // Public resources are always accessible
  if (rule.visibility === "public") {
    return { allowed: true };
  }

  // All non-public resources require authentication
  if (!context.user) {
    return {
      allowed: false,
      reason: "Authentication required",
    };
  }

  const { user } = context;

  switch (rule.visibility) {
    case "private": {
      // Check role-based access
      const hasRole = rule.requiredRoles.some((role) =>
        user.roles.includes(role)
      );
      if (!hasRole) {
        return {
          allowed: false,
          reason: `Requires one of roles: ${rule.requiredRoles.join(", ")}`,
        };
      }
      return { allowed: true };
    }

    case "user-owned": {
      // Check if user owns the resource
      if (rule.ownerAttribute) {
        const ownerValue = context.params[rule.ownerAttribute];
        if (ownerValue === user.id) {
          return { allowed: true };
        }
      }

      // If owner-managed access is enabled, return UMA ticket hint
      if (rule.ownerManagedAccess) {
        return {
          allowed: false,
          reason: "Owner consent required",
          umaTicket: `uma-ticket:${rule.resourceKey}`,
        };
      }

      return { allowed: false, reason: "Access denied" };
    }

    case "consent": {
      // Consent-based resources require UMA flow
      return {
        allowed: false,
        reason: "Consent required",
        umaTicket: `uma-ticket:${rule.resourceKey}`,
      };
    }

    default:
      return { allowed: false, reason: "Unknown visibility" };
  }
}
